import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { useQuery } from "react-query"

import Autocomplete from "src/components/Autocomplete"
import Form from "src/components/Form"

import { queryVendors } from "src/api/ManageItems"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

import { validateTextLength } from "./helpers"

const VendorAndRefNumFields = ({ accountingV2Enabled = false }) => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext()

  const [query, setQuery] = useState("")
  const marinaSlug = getCurrentMarinaSlug()

  const vendorsQuery = useQuery({
    queryKey: ["vendors", marinaSlug],
    queryFn: () => queryVendors({ marinaSlug }),
    enabled: accountingV2Enabled,
  })

  const vendors = useMemo(
    () => vendorsQuery.data?.map((vendor) => ({ name: vendor.name })) || [],
    [vendorsQuery.data]
  )

  const filteredVendors = useMemo(
    () =>
      query === ""
        ? vendors
        : vendors.filter((vendor) =>
            vendor.name.toLowerCase().includes(query.toLowerCase())
          ),
    [query, vendors]
  )

  // Add "Create new vendor" option if query doesn't exactly match an existing vendor
  const vendorOptions = useMemo(
    () =>
      query.length > 0 &&
      !vendors.some((v) => v.name.toLowerCase() === query.toLowerCase())
        ? [
            { id: `create-${query}`, name: query, isNew: true },
            ...filteredVendors,
          ]
        : filteredVendors,
    [query, vendors, filteredVendors]
  )

  return (
    <div className="mt-4 flex w-full flex-row space-x-4">
      <div className="flex-1">
        <Form.Label htmlFor="vendor">Vendor</Form.Label>
        {accountingV2Enabled ? (
          <Controller
            name="vendor"
            control={control}
            rules={{
              required: "Vendor is required",
              validate: (text) => validateTextLength({ text, maxLength: 1000 }),
            }}
            render={({ field }) => (
              <Autocomplete
                id="vendor"
                selectedItem={
                  field.value ? { id: field.value, name: field.value } : null
                }
                selectedItemInputValue={field.value}
                onSelect={(selected) => {
                  setValue("vendor", selected?.name || "", {
                    shouldValidate: true,
                  })
                }}
                onInputChange={(value) => {
                  setValue("vendor", value, { shouldValidate: true })
                  setQuery(value)
                }}
                options={vendorOptions}
                placeholder="Select or type vendor name"
                isLoading={vendorsQuery.isFetching}
                clearable
                renderOption={({ option }) => {
                  if (option.isNew) {
                    return (
                      <span>
                        Create{" "}
                        <span className="font-semibold">
                          &ldquo;{option.name}&rdquo;
                        </span>
                      </span>
                    )
                  }
                  return <span>{option.name}</span>
                }}
              />
            )}
          />
        ) : (
          <Form.TextField
            id="vendor"
            {...register("vendor", {
              validate: (text) => validateTextLength({ text, maxLength: 1000 }),
            })}
            hasErrors={Boolean(errors.vendor)}
          />
        )}
        <Form.Error>{errors.vendor?.message}</Form.Error>
      </div>
      <div className="flex-1">
        <Form.Label htmlFor="ref-num">Reference #</Form.Label>
        <Form.TextField
          id="ref-num"
          {...register("refNum", {
            required: accountingV2Enabled && "Reference # is required",
            validate: (text) => validateTextLength({ text, maxLength: 1000 }),
          })}
          hasErrors={Boolean(errors.refNum)}
        />
        <Form.Error>{errors.refNum?.message}</Form.Error>
      </div>
    </div>
  )
}

VendorAndRefNumFields.propTypes = {
  accountingV2Enabled: PropTypes.bool,
}

export default VendorAndRefNumFields
